import React from "react";
import { CheckCircle, AlertCircle, Info, X } from "lucide-react";
import { getNotificationClasses, getColorScheme } from "../config/colors";
import { useTranslation } from "react-i18next";
import { getDirection, getTextAlign } from "../utils/rtl";

export type AlertType = "success" | "error" | "warning" | "info";

export interface AlertProps {
  type?: AlertType;
  title?: string;
  message: string | React.ReactNode;
  isDark?: boolean;
  onClose?: () => void;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  showCloseButton?: boolean;
  className?: string;
}

const Alert: React.FC<AlertProps> = ({
  type = "info",
  title,
  message,
  isDark = false,
  onClose,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  showCloseButton = true,
  className = "",
}) => {
  const { i18n } = useTranslation();
  const direction = getDirection(i18n.language);
  const textAlign = getTextAlign(i18n.language);
  const colors = getNotificationClasses(type, isDark);
  const scheme = getColorScheme();
  const primary = scheme.primaryShades[600];
  const primaryHover = scheme.primaryShades[700];

  const renderIcon = () => {
    const textColor =
      colors.split(" ").find((c) => c.startsWith("text-")) || "text-gray-600";
    switch (type) {
      case "success":
        return <CheckCircle className={`w-5 h-5 ${textColor}`} />;
      case "error":
      case "warning":
        return <AlertCircle className={`w-5 h-5 ${textColor}`} />;
      default:
        return <Info className={`w-5 h-5 ${textColor}`} />;
    }
  };

  return (
    <div
      className={`rounded-lg border ${colors} shadow-sm p-4 flex items-start gap-3 ${className}`}
      role="alert"
      dir={direction}
    >
      <div className="mt-0.5">{renderIcon()}</div>
      <div className="flex-1 min-w-0">
        {title && (
          <div className={`font-semibold mb-0.5 ${colors}`} style={{ textAlign }}>{title}</div>
        )}
        <div className={`text-sm leading-relaxed ${
          colors.split(" ").find((c) => c.startsWith("text-")) || ""
        }`} style={{ textAlign }}>{message}</div>
        {(onConfirm || onCancel) && (
          <div className={`mt-3 flex gap-2`} style={{ flexDirection: direction === 'rtl' ? 'row-reverse' : 'row' }}>
            {onCancel && (
              <button
                onClick={onCancel}
                className={`px-3 py-1.5 rounded-lg text-sm border ${
                  isDark
                    ? "border-neutral-600 hover:bg-neutral-800 text-neutral-200"
                    : "border-gray-300 hover:bg-gray-100 text-gray-800"
                }`}
                style={{ textAlign }}
              >
                {cancelText || "Cancel"}
              </button>
            )}
            {onConfirm && (
              <button
                onClick={onConfirm}
                className={`px-3 py-1.5 rounded-lg text-sm text-white bg-${primary} hover:bg-${primaryHover}`}
                style={{ textAlign }}
              >
                {confirmText || "OK"}
              </button>
            )}
          </div>
        )}
      </div>
      {showCloseButton && onClose && (
        <button
          onClick={onClose}
          className={`p-1 rounded-md ${
            isDark ? "hover:bg-neutral-800" : "hover:bg-gray-100"
          }`}
          aria-label="Close"
        >
          <X className="w-4 h-4 opacity-70" />
        </button>
      )}
    </div>
  );
};

export default Alert;
